import { Link, useLocation } from "react-router-dom";
import useSWR from "swr";
import { AnimatePresence, motion } from "framer-motion";
import { Columns3, X } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { useCompare, MAX_COMPARE } from "@/context/CompareContext";

export const CompareButton = ({ item, full = false }) => {
  const { has, toggle } = useCompare();
  const on = has(item.id);
  const click = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(item);
  };
  if (full)
    return (
      <button onClick={click} data-testid="item-compare-btn"
        className={`h-12 inline-flex items-center justify-center gap-2 rounded-full px-6 text-sm border transition-colors duration-300 ${on ? "bg-[#F5F3EF] text-[#0D0D11] border-[#F5F3EF]" : "border-[#D4AF37]/50 text-[#E5C158] hover:bg-[#D4AF37]/10"}`}>
        <Columns3 className="w-4 h-4" /> {on ? "In comparison" : "Compare"}
      </button>
    );
  return (
    <motion.button whileTap={{ scale: 0.85 }} onClick={click} data-testid={`compare-toggle-${item.id}`}
      aria-label={on ? "Remove from comparison" : "Add to comparison"}
      className={`w-10 h-10 grid place-items-center rounded-full backdrop-blur-md border transition-colors duration-300 ${on ? "bg-[#F5F3EF] border-[#F5F3EF] text-[#0D0D11]" : "bg-[#0D0D11]/60 border-[#D4AF37]/40 text-[#E5C158] hover:bg-[#D4AF37]/20"}`}>
      <Columns3 className="w-4 h-4" />
    </motion.button>
  );
};

export const CompareTray = () => {
  const { ids, remove, clear, category } = useCompare();
  const { pathname } = useLocation();
  const { data } = useSWR(ids.length ? `/items?category=${category}` : null, fetcher);
  const items = ids.map((id) => asList(data).find((i) => i.id === id)).filter(Boolean);
  const show = items.length > 0 && pathname !== "/compare";

  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }} transition={{ type: "spring", damping: 24 }}
          className="no-print fixed bottom-4 inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 z-40 flex items-center gap-3 rounded-full border border-[#D4AF37]/30 bg-[#13141C]/90 backdrop-blur-xl p-2 pl-3 shadow-2xl"
          data-testid="compare-tray">
          <div className="flex -space-x-2">
            {Array.from({ length: MAX_COMPARE }).map((_, i) => (
              items[i] ? (
                <button key={items[i].id} onClick={() => remove(items[i].id)} aria-label={`Remove ${items[i].name}`} className="group relative w-11 h-11 rounded-full overflow-hidden border-2 border-[#13141C]">
                  <img src={imgUrl(items[i].images?.[0])} alt={items[i].name} className="w-full h-full object-cover" />
                  <span className="absolute inset-0 grid place-items-center bg-[#0D0D11]/70 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></span>
                </button>
              ) : (
                <span key={i} className="w-11 h-11 rounded-full border-2 border-dashed border-[#D4AF37]/30 bg-[#0D0D11]" />
              )
            ))}
          </div>
          <span className="hidden sm:inline text-sm text-[#A09E97] px-2">{items.length} of {MAX_COMPARE}</span>
          <button onClick={clear} data-testid="compare-tray-clear" className="text-xs text-[#A09E97] hover:text-[#F5F3EF] px-2">Clear</button>
          <Link to="/compare" data-testid="compare-tray-open"
            className={`ml-auto h-11 inline-flex items-center gap-2 rounded-full px-6 text-sm font-medium transition-colors ${items.length > 1 ? "bg-[#D4AF37] text-[#0D0D11] hover:bg-[#E5C158]" : "bg-[#D4AF37]/30 text-[#F5F3EF] pointer-events-none"}`}>
            <Columns3 className="w-4 h-4" /> {items.length > 1 ? "Compare" : "Pick one more"}
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
